import React, { useState, useEffect, Suspense, lazy } from "react";
import Loader from "../components/Loader";
import Nav from "../components/Nav";
import Cursor from "../components/Cursor";
import AmbientContinuum from "../components/AmbientContinuum";
import EvolutionScroll from "../components/EvolutionScroll";
import Reveal from "../components/Reveal";
import { motion } from "framer-motion";
import { usePersona } from "../lib/persona";
import { useAdaptive } from "../lib/adaptive";
import { ArrowDown } from "lucide-react";
import { sendTelemetry } from "../lib/api";
import { speak } from "../lib/audio";

// Lazy-load heavy below-the-fold sections
const Backbone3D = lazy(() => import("../components/Backbone3D"));
const LivingFabric = lazy(() => import("../components/LivingFabric"));
const AgentSimulator = lazy(() => import("../components/AgentSimulator"));
const Products = lazy(() => import("../components/Products"));
const DevExperience = lazy(() => import("../components/DevExperience"));
const Sandbox = lazy(() => import("../components/Sandbox"));
const CommandCenter = lazy(() => import("../components/CommandCenter"));
const SingularityChamber = lazy(() => import("../components/SingularityChamber"));
const FinalCTA = lazy(() => import("../components/FinalCTA"));

function SectionFallback() {
    return (
        <div className="border-t border-border bg-void h-[40vh] flex items-center justify-center" aria-busy="true">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-signal animate-pulse">› loading section</div>
        </div>
    );
}

export default function Home() {
    const [loaded, setLoaded] = useState(() => {
        const sessionFlag = sessionStorage.getItem("apib_loaded") === "1";
        const recent = (Date.now() - Number(localStorage.getItem("apib_loaded_ts") || 0)) < 1000 * 60 * 60 * 24;
        return sessionFlag || recent;
    });
    const { persona } = usePersona();
    const { returningVisitor, activeSection } = useAdaptive();

    useEffect(() => {
        if (loaded) {
            sessionStorage.setItem("apib_loaded", "1");
            localStorage.setItem("apib_loaded_ts", String(Date.now()));
            sendTelemetry("page_view", { path: "/", persona, returning: returningVisitor }).catch(() => {});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loaded]);

    useEffect(() => {
        if (!loaded) return;
        sendTelemetry("persona_switch", { persona }).catch(() => {});
    }, [persona, loaded]);

    // Cinematic voice events tied to section transitions (only spoken once each)
    useEffect(() => {
        if (!loaded || !activeSection) return;
        const lines = {
            reveal: ["Distributed intelligence connected.", "voice:reveal"],
            backbone: ["Backbone synchronised.", "voice:backbone"],
            fabric: ["Autonomous fabric online.", "voice:fabric"],
            simulator: ["Witness intelligence form.", "voice:sim"],
            command: ["Operations cockpit engaged.", "voice:cmd"],
            singularity: ["Intelligence requires infrastructure.", "voice:sing"],
        };
        const ev = lines[activeSection];
        if (ev) speak(ev[0], ev[1]);
        if (activeSection === "fabric" || activeSection === "command" || activeSection === "singularity") {
            sendTelemetry(`section_${activeSection}`, {}).catch(() => {});
        }
    }, [activeSection, loaded]);

    useEffect(() => {
        if (loaded && returningVisitor) {
            // welcome back voice
            setTimeout(() => speak("Welcome back, operator.", "voice:welcomeback"), 1200);
        }
    }, [loaded, returningVisitor]);

    return (
        <div className="min-h-screen bg-void text-ghost grain">
            <AmbientContinuum />
            <Cursor />
            {!loaded && <Loader onDone={() => setLoaded(true)} />}
            <Nav />
            <main id="main" className="pt-20">
                <Hero />
                <EvolutionScroll />
                <Reveal />
                <Suspense fallback={<SectionFallback />}><Backbone3D /></Suspense>
                <Suspense fallback={<SectionFallback />}><LivingFabric /></Suspense>
                <Suspense fallback={<SectionFallback />}><AgentSimulator /></Suspense>
                <Suspense fallback={<SectionFallback />}><Products /></Suspense>
                <Suspense fallback={<SectionFallback />}><DevExperience /></Suspense>
                <Suspense fallback={<SectionFallback />}><Sandbox /></Suspense>
                <Suspense fallback={<SectionFallback />}><CommandCenter /></Suspense>
                <Suspense fallback={<SectionFallback />}><SingularityChamber /></Suspense>
                <Suspense fallback={<SectionFallback />}><FinalCTA /></Suspense>
            </main>
        </div>
    );
}

function Hero() {
    const { persona } = usePersona();
    const personaCopy = {
        developer: "Build autonomous agents in five lines of code.",
        enterprise: "Operate every workflow under verified governance.",
        investor: "Infrastructure for the next $1T compute era.",
        researcher: "An open fabric for studying multi-agent systems.",
    };
    return (
        <section id="hero" className="relative min-h-[88vh] flex flex-col justify-end overflow-hidden" data-testid="phase-hero">
            <div className="absolute inset-0 bg-grid-amber opacity-25" />
            <HeroParticles />
            <HeroIcosahedron />
            <div className="absolute inset-0 vignette" />

            <div className="relative z-10 px-6 sm:px-12 lg:px-20 pb-20">
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.4 }} className="max-w-6xl">
                    <div className="text-[10px] font-mono uppercase tracking-[0.5em] text-signal mb-6 flex items-center gap-3">
                        <span className="w-2 h-2 bg-signal animate-pulse-glow" />
                        APIB · BACKBONE ONLINE
                    </div>
                    <h1 className="font-display font-black uppercase leading-[0.85] tracking-[-0.04em] fluid-display-xl">
                        The autonomous<br />internet is <span className="text-signal glow-amber font-editorial italic font-normal tracking-tight">coming.</span>
                    </h1>
                    <div className="mt-8 max-w-2xl">
                        <p className="font-mono text-sm sm:text-base text-muted leading-relaxed">
                            APIB is the operating system for autonomous intelligence — orchestrating billions of agents across a single, governed, intelligent fabric.
                        </p>
                        <p className="mt-3 font-mono text-sm text-signal" data-testid="hero-persona-line">› {personaCopy[persona]}</p>
                    </div>
                    <div className="mt-10 flex flex-col sm:flex-row gap-3 flex-wrap">
                        <a href="#simulator" data-testid="hero-cta-simulator" data-cursor-label="LAUNCH" className="btn-signal inline-flex items-center gap-2 px-5 py-3 font-mono uppercase tracking-[0.2em] text-xs">
                            Witness intelligence form
                        </a>
                        <a href="#fabric" data-testid="hero-cta-fabric" data-cursor-label="OBSERVE" className="btn-ghost inline-flex items-center gap-2 px-5 py-3 text-ghost font-mono uppercase tracking-[0.2em] text-xs">
                            Watch the Living Fabric
                        </a>
                        <a href="#sandbox" data-testid="hero-cta-sandbox" data-cursor-label="WIRE" className="btn-ghost inline-flex items-center gap-2 px-5 py-3 text-ghost font-mono uppercase tracking-[0.2em] text-xs">
                            Wire your own backbone
                        </a>
                    </div>
                </motion.div>

                <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 2, repeat: Infinity }} className="mt-16 inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-muted">
                    <ArrowDown size={12} /> Scroll to enter  ·  press <kbd className="px-1 border border-border text-signal mx-1">⌘K</kbd> to jump
                </motion.div>
            </div>
        </section>
    );
}

function HeroIcosahedron() {
    // Slow-rotating wireframe ornament in the negative space
    return (
        <div className="hidden lg:block absolute top-1/2 right-12 -translate-y-1/2 w-[480px] h-[480px] pointer-events-none opacity-70">
            <svg viewBox="-1 -1 2 2" className="w-full h-full animate-spin-slow" style={{ filter: "drop-shadow(0 0 40px rgba(255,176,0,0.18))" }}>
                <defs>
                    <radialGradient id="ico-glow" cx="50%" cy="50%">
                        <stop offset="0%" stopColor="#FFB000" stopOpacity="0.0" />
                        <stop offset="100%" stopColor="#FFB000" stopOpacity="0.0" />
                    </radialGradient>
                </defs>
                {/* Outer faceted icosahedron-ish projection */}
                {Array.from({ length: 12 }).map((_, i) => {
                    const a = (i / 12) * Math.PI * 2;
                    const x = Math.cos(a) * 0.85;
                    const y = Math.sin(a) * 0.85;
                    return <circle key={i} cx={x} cy={y} r="0.012" fill="#FFB000" />;
                })}
                {Array.from({ length: 12 }).map((_, i) => Array.from({ length: 12 }).map((_, j) => {
                    if (j <= i) return null;
                    const a1 = (i / 12) * Math.PI * 2;
                    const a2 = (j / 12) * Math.PI * 2;
                    return (
                        <line
                            key={`${i}-${j}`}
                            x1={Math.cos(a1) * 0.85} y1={Math.sin(a1) * 0.85}
                            x2={Math.cos(a2) * 0.85} y2={Math.sin(a2) * 0.85}
                            stroke="#FFB000" strokeOpacity={Math.abs(i - j) === 6 ? "0.45" : "0.08"} strokeWidth="0.0035"
                        />
                    );
                }))}
                <circle cx="0" cy="0" r="0.5" stroke="#3DD9D6" strokeOpacity="0.12" strokeWidth="0.004" fill="none" />
                <circle cx="0" cy="0" r="0.3" stroke="#FFFFFF" strokeOpacity="0.10" strokeWidth="0.003" fill="none" />
                <circle cx="0" cy="0" r="0.04" fill="#FFB000" />
            </svg>
        </div>
    );
}

function HeroParticles() {
    return (
        <svg className="absolute inset-0 w-full h-full opacity-50" viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
            {Array.from({ length: 110 }).map((_, i) => {
                const x = (i * 173) % 1200;
                const y = (i * 91) % 800;
                return (
                    <g key={i}>
                        <circle cx={x} cy={y} r={1 + (i % 3) * 0.5} fill="#FFB000">
                            <animate attributeName="opacity" values="0;1;0" dur={`${2 + (i % 5)}s`} begin={`${i * 0.03}s`} repeatCount="indefinite" />
                        </circle>
                    </g>
                );
            })}
            {Array.from({ length: 18 }).map((_, i) => {
                const x1 = (i * 67) % 1200, y1 = (i * 137) % 800;
                const x2 = ((i + 3) * 67) % 1200, y2 = ((i + 3) * 137) % 800;
                return <line key={`l-${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#FFB000" strokeOpacity="0.18" strokeWidth="0.5" />;
            })}
        </svg>
    );
}
